import React from "react";
import Icon from "./AppIcon";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    error.__ErrorBoundary = true;
    window.__COMPONENT_ERROR__?.(error, errorInfo);
    this.setState({ error, errorInfo });
  }

  toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  copyDetails = async () => {
    const { error, errorInfo } = this.state;
    const payload = [
      `Location: ${typeof window !== 'undefined' ? window.location.href : 'unknown'}`,
      `UA:       ${typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'}`,
      `When:     ${new Date().toISOString()}`,
      '',
      `Error:    ${error?.name || 'Error'}: ${error?.message || String(error)}`,
      '',
      'Stack:',
      error?.stack || '(no stack)',
      '',
      'Component stack:',
      errorInfo?.componentStack || '(no component stack)',
    ].join('\n');

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      }
    } catch {
      // ignore — some browsers block programmatic clipboard access
    }
  };

  render() {
    if (this.state?.hasError) {
      const { error, errorInfo, showDetails } = this.state;
      const message = error?.message || String(error || 'Unknown error');

      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
          <div className="text-center max-w-lg w-full">
            <div className="flex justify-center items-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="42px" height="42px" viewBox="0 0 32 33" fill="none">
                <path d="M16 28.5C22.6274 28.5 28 23.1274 28 16.5C28 9.87258 22.6274 4.5 16 4.5C9.37258 4.5 4 9.87258 4 16.5C4 23.1274 9.37258 28.5 16 28.5Z" stroke="#343330" strokeWidth="2" strokeMiterlimit="10" />
                <path d="M11.5 15.5C12.3284 15.5 13 14.8284 13 14C13 13.1716 12.3284 12.5 11.5 12.5C10.6716 12.5 10 13.1716 10 14C10 14.8284 10.6716 15.5 11.5 15.5Z" fill="#343330" />
                <path d="M20.5 15.5C21.3284 15.5 22 14.8284 22 14C22 13.1716 21.3284 12.5 20.5 12.5C19.6716 12.5 19 13.1716 19 14C19 14.8284 19.6716 15.5 20.5 15.5Z" fill="#343330" />
                <path d="M21 22.5C19.9625 20.7062 18.2213 19.5 16 19.5C13.7787 19.5 12.0375 20.7062 11 22.5" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="flex flex-col gap-1 text-center">
              <h1 className="text-2xl font-medium text-neutral-800">Something went wrong</h1>
              <p className="text-neutral-600 text-base w-10/12 mx-auto">
                The page hit an unexpected error. Nothing you did is broken — you can head back and keep working.
              </p>
            </div>

            {/* Error preview — the actual message, always visible. Users can
                screenshot this line and send it to us; that's usually enough
                for us to identify the bug in one look. */}
            <div className="mt-4 text-left bg-white border border-neutral-200 rounded-md p-3 text-sm text-neutral-700 break-words">
              <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 mb-1">Error</p>
              <p className="font-mono text-xs">{message}</p>
            </div>

            <div className="flex justify-center items-center gap-2 mt-6 flex-wrap">
              <button
                onClick={() => {
                  window.location.href = "/";
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded flex items-center gap-2 transition-colors duration-200 shadow-sm"
              >
                <Icon name="ArrowLeft" size={18} color="#fff" />
                Back to home
              </button>
              <button
                onClick={() => {
                  if (typeof window !== 'undefined') window.location.reload();
                }}
                className="bg-white border border-neutral-300 hover:bg-neutral-100 text-neutral-800 font-medium py-2 px-4 rounded flex items-center gap-2 transition-colors duration-200 shadow-sm"
              >
                <Icon name="RefreshCw" size={16} />
                Reload page
              </button>
              <button
                onClick={this.toggleDetails}
                className="bg-white border border-neutral-300 hover:bg-neutral-100 text-neutral-800 font-medium py-2 px-4 rounded flex items-center gap-2 transition-colors duration-200 shadow-sm"
              >
                <Icon name={showDetails ? 'ChevronUp' : 'ChevronDown'} size={16} />
                {showDetails ? 'Hide details' : 'Show details'}
              </button>
            </div>

            {showDetails && (
              <div className="mt-4 text-left bg-white border border-neutral-200 rounded-md p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                    Diagnostic details
                  </p>
                  <button
                    type="button"
                    onClick={this.copyDetails}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Copy to clipboard
                  </button>
                </div>
                <pre className="text-[10px] leading-tight whitespace-pre-wrap font-mono text-neutral-700 max-h-64 overflow-auto">
{`${error?.name || 'Error'}: ${message}

${error?.stack || '(no stack trace)'}

Component stack:
${errorInfo?.componentStack || '(no component stack)'}`}
                </pre>
                <p className="text-[10px] text-neutral-500 mt-2">
                  Screenshot this box and share it with your admin — it identifies exactly which file and line threw the error.
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props?.children;
  }
}

export default ErrorBoundary;
